"use client";

// $1 trial offer modal (PANDA-1500) — the three acquisition surfaces from the
// design (panda_trial_sequence_v1.html), rebased to today's Pro ($29/300):
//   • variant "ad"      → T1 ad-led / T3 exit-intent hero (marketable hook +
//                          the honest 3-day timeline that doubles as the
//                          renewal disclosure — DMCC/FTC).
//   • variant "dismiss" → T2 in-product fallback after the full-price modal is
//                          dismissed, personalised from the blocked build.
//
// Presentational: the parent owns eligibility (useTrialOffer) and passes the
// offer copy + start handler. Every price/credit figure comes from the offer
// object (lib/trial/config.ts) so the modal can never contradict the real price.
//
// Visual system (PANDA-1500 follow-up): mirrors the canonical billing/upgrade
// modal — the rated credits flow (components/billing/rated) — so the trial reads
// as part of the same product: shared @base-ui dialog foundation, the same shell
// (rounded-3xl card / full-screen sheet on mobile / z-[100]), the ringed plan
// card, emerald benefit checks, a gradient primary CTA at rounded-xl, and the
// same type scale, and — since PANDA-1779 — the same emerald accent hue: green
// CTA (`success` variant) and an emerald plan-card ring. This modal got that
// treatment first (PANDA-1778); the audit then found the brand orange→red
// gradient on the primary CTA of nearly every other non-destructive modal and
// moved them all to match. The shared policy lives in
// `components/ui/modal-accents.ts`.

import { useEffect, useState } from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { X, Check, ShieldCheck, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  MODAL_SCROLL_BODY,
  MODAL_VIEWPORT_FRAME_SM,
} from "@/components/ui/modal-surface";
import { Button } from "@/components/ui/button";
import type { TrialOfferCopy, TrialProjectUsage } from "@/hooks/use-trial-offer";
import {
  showsUpgradeCopyVariant,
  type UpgradeCopyVariant,
} from "@/lib/billing/upgrade-copy-experiment";
import { PRO_BENEFITS_VARIANT } from "@/lib/billing/pro-benefits";

interface TrialOfferModalProps {
  open: boolean;
  onClose: () => void;
  offer: TrialOfferCopy;
  variant: "ad" | "dismiss";
  /** Personalised headline (T2 — laddered up from the blocked build prompt). */
  headline?: string | null;
  subheading?: string | null;
  onStart: () => Promise<void> | void;
  /** Fire once when the modal actually renders (exposure + 7-day anchor). */
  onShown?: () => void;
  /** Copy A/B arm, resolved server-side. Defaults to the baseline so any caller
   *  that hasn't been wired yet renders today's copy, never a half-applied
   *  rewrite. */
  copyVariant?: UpgradeCopyVariant;
  /** Free-plan project usage for the "N/3 projects used" trigger chip. Null
   *  when unknown — the chip is then omitted rather than guessed. */
  projects?: TrialProjectUsage | null;
  /** Soft-exit ("Not now — stay on Free"). Falls back to onClose; kept separate
   *  so callers can tell a deliberate decline from an X/backdrop dismiss. */
  onDecline?: () => void;
}

export function TrialOfferModal({
  open,
  onClose,
  offer,
  variant,
  headline,
  subheading,
  onStart,
  onShown,
  copyVariant = "control",
  projects,
  onDecline,
}: TrialOfferModalProps) {
  const [starting, setStarting] = useState(false);
  const rewrite = showsUpgradeCopyVariant(copyVariant);

  useEffect(() => {
    if (open) onShown?.();
    // onShown is guarded against re-fire by the hook (shownRef).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Variant arm: one headline for both surfaces. The old pair led on the $1
  // without naming what it buys ("Build your first app for $1"), which is
  // exactly the framing the QA test blames for the 3-second dismiss — it reads
  // as a cheap starter product rather than full Pro at a reduced entry price.
  // "full Pro" is the whole point, so it goes in the headline.
  const defaultHeadline = rewrite ? (
    <>
      Try full Pro{" "}
      <span className="text-emerald-500 dark:text-emerald-400">free</span> for{" "}
      {offer.days} days
    </>
  ) : variant === "ad" ? (
    <>
      Build your first app{" "}
      <span className="text-emerald-500 dark:text-emerald-400">free</span> for{" "}
      {offer.days} days
    </>
  ) : (
    "Keep building — free for 3 days"
  );

  // The price + trial terms, stated ONCE, directly under the headline: duration,
  // today's charge, the ongoing charge, and cancellation. The control arm states
  // the renewal only in a small muted line at the bottom of the offer card
  // (offer.renewalLine, still rendered below for control) — the "bait-and-switch"
  // read the QA test set out to fix. Figures come from the offer object, so this
  // can never contradict what Stripe actually charges.
  // At $0 this line carries the whole negative-option disclosure: card, amount
  // and the DAY it is taken. "Free today, then $29/mo" would omit the fact that
  // a payment method is stored now, which is the part a customer disputes.
  const termsLine = `Pro trial · free for ${offer.days} days — card required, then ${offer.convertPriceLabel}/mo from day ${offer.days}. Cancel any time.`;

  // In the variant arm the terms line IS the sub-copy, so there is no separate
  // default sub — but a personalised T2 subheading still renders above it (see
  // the JSX). The terms line is never conditional on that: it carries the
  // renewal disclosure (DMCC/FTC), so it must survive personalisation.
  const defaultSub = rewrite
    ? null
    : variant === "ad"
      ? `${offer.credits} credits to build and launch over your first ${offer.days} days. Enough for a full app, a landing page, or your first campaign.`
      : `Get ${offer.credits} credits and ${offer.days} days to turn your idea into something live.`;

  // "N/3 projects used" — the concrete cap an active free builder has already
  // hit, and the strongest upgrade trigger available. Rendered only from a real
  // server-side count; never inferred.
  const showProjectChip =
    rewrite && !!projects && projects.limit > 0 && projects.current > 0;

  async function handleStart() {
    setStarting(true);
    try {
      await onStart();
    } finally {
      // If start() redirected, this component unmounts; otherwise re-enable.
      setStarting(false);
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        {/* z-[100]: above the in-project chat panel (z-60) so the offer is never
            trapped behind it — matches the rated credits modal's layer. */}
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[100] bg-black/50 supports-backdrop-filter:backdrop-blur-sm",
            "data-open:animate-in data-open:fade-in-0",
            "data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            // Phones: full-screen sheet. ≥sm: centered card capped to the viewport.
            // The popup is the FRAME, never the scroller — the card inside owns
            // the one scroll region, which keeps the ✕ pinned while the body
            // scrolls. Capped to the DYNAMIC viewport so a mobile browser's
            // visible toolbars can't push the card off screen. (PANDA-1565)
            "fixed inset-0 z-[100] flex flex-col overflow-hidden sm:rounded-3xl",
            "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-[calc(100%-2rem)] sm:max-w-140 sm:-translate-x-1/2 sm:-translate-y-1/2",
            MODAL_VIEWPORT_FRAME_SM,
            "data-open:animate-in data-open:fade-in-0 sm:data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 sm:data-closed:zoom-out-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Start your free Pro trial
          </DialogPrimitive.Title>

          <div
            className={cn(
              "relative flex w-full max-h-full flex-col overflow-hidden bg-card text-card-foreground",
              // Mirrors the reference Shell (rated-credits-flow) exactly: full-bleed
              // sheet on phones, re-capped to a centered 35rem card from sm up.
              "h-full max-w-none rounded-none shadow-none ring-0 sm:h-auto sm:max-w-140 sm:rounded-3xl sm:shadow-2xl sm:ring-1 sm:ring-foreground/10",
            )}
          >
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 z-20 rounded-full bg-card/80 text-foreground/60 backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-5" />
            </Button>
            {/* The one scroll region. Padding lives here (not on the card) so the
                pinned ✕ above stays put while this scrolls, and the scrollbar
                runs the card's full height. Tightened on short viewports so the
                common laptop case needs no scroll at all. (PANDA-1565) */}
            <div
              className={cn(
                MODAL_SCROLL_BODY,
                "p-6 short:p-5 sm:p-8 sm:short:p-6",
              )}
            >

              {/* Variant arm leads with the project cap the user has actually hit
                  — a fact about their account, not a marketing line — and falls
                  back to the surface badge when the count is unknown. */}
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-extrabold text-emerald-400">
                <span className="size-1.5 rounded-full bg-emerald-400" />
                {showProjectChip
                  ? `${projects!.current}/${projects!.limit} projects used`
                  : variant === "ad"
                    ? "New builder offer"
                    : "Keep your momentum"}
              </div>

              <h2 className="pr-8 text-3xl font-extrabold leading-tight tracking-tight">
                {headline || defaultHeadline}
              </h2>
              {(subheading || defaultSub) && (
                <p className="mt-2.5 text-[15px] font-medium text-muted-foreground">
                  {subheading || defaultSub}
                </p>
              )}
              {/* Price + trial terms, said once, right under the headline. Not
                  conditional on `subheading`: this carries the renewal disclosure,
                  so a personalised T2 subheading must never displace it. */}
              <p className="mt-2.5 text-[15px] font-semibold text-foreground">
                {termsLine}
              </p>

              {/* Offer card — same plan card as the upgrade modal, but ringed in
                  emerald rather than the brand orange: this is the trial's
                  success framing, not a paid upgrade. emerald-600 is 5.0:1 on the
                  dark card / 3.7:1 on the light one, so the ring stays visible in
                  both themes. The upgrade + trial-exhausted modals keep orange. */}
              <div className="mt-5 rounded-2xl p-5 ring-2 ring-emerald-600">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-extrabold tracking-tight">
                    Pro trial
                  </h3>
                  <span className="rounded-full bg-panda-gradient-success px-2.5 py-1 text-[11px] font-extrabold text-white">
                    {offer.days} days
                  </span>
                </div>

                <p className="mt-3.5 text-[13px] font-extrabold">
                  Your free trial gets you:
                </p>
                {/* Variant arm renders the SHARED Pro feature set
                    (lib/billing/pro-benefits.ts) — the same list the direct
                    upgrade modal shows — so the $1 trial reads as full Pro at a
                    reduced entry price rather than a lesser product. The trial's
                    own credit grant is prepended as a distinct line because it is
                    a trial term, not a Pro feature: trial credits expire with the
                    trial (see the fine print below), which is why this line
                    deliberately does NOT claim they roll over. */}
                <ul className="mt-3 grid gap-2.5">
                  {(rewrite
                    ? [
                        `${offer.credits} trial credits to use immediately`,
                        ...PRO_BENEFITS_VARIANT,
                      ]
                    : [
                        `${offer.credits} credits, ready to spend now`,
                        "Publish to your own custom domain",
                        "Every project you build is yours to keep",
                      ]
                  ).map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[13.5px] font-medium"
                    >
                      <Check className="mt-0.5 size-4 flex-none text-emerald-400" />
                      {b}
                    </li>
                  ))}
                </ul>

                {/* Locked credit row — trial amount, not a dropdown */}
                <div className="mt-4 flex h-11 items-center justify-between rounded-xl border border-input bg-secondary px-3.5 text-sm font-bold">
                  <span>
                    {offer.credits} credits · {offer.days} days
                  </span>
                  <span className="text-emerald-500 dark:text-emerald-400">
                    {offer.priceLabel}
                  </span>
                </div>

                {variant === "ad" && (
                  <TrialTimeline offer={offer} rewrite={rewrite} />
                )}

                <div className="mt-4 flex items-baseline justify-between border-t border-border pt-4">
                  <span className="text-[13px] font-semibold text-muted-foreground">
                    Due today
                  </span>
                  <span className="text-[27px] font-extrabold leading-none tracking-tight">
                    {offer.priceLabel}
                  </span>
                </div>
                {/* No small muted renewal line here any more. It used to be
                    the control arm's ONLY disclosure — 12px, right-aligned,
                    under a 27px "Free" — which is the buried framing this modal
                    was reported for. The full terms now render once, at full
                    weight, next to the CTA in every arm, so repeating them here
                    would only be a second, weaker copy of the same fact. */}
              </div>

              {/* Green, not the brand orange→red: this CTA sits under the
                  "Keep your momentum" / emerald success framing, and the
                  gradient primary reads as a warning next to it. The green
                  gradient + hover/disabled behaviour lives in the `success`
                  button variant so every state stays in one place. */}
              <Button
                size="lg"
                variant="success"
                onClick={handleStart}
                disabled={starting}
                aria-busy={starting}
                className={cn(
                  "mt-6 h-12 w-full rounded-xl text-[15px]",
                  // "Redirecting to checkout…" is status the user needs to read, so
                  // the loading state keeps full opacity — the shared
                  // `disabled:opacity-50` would drop that label to 2.2:1 (light)
                  // / 3.1:1 (dark), under the 4.5:1 minimum. `disabled` still
                  // blocks interaction; cursor-wait says why.
                  starting && "disabled:cursor-wait disabled:opacity-100",
                )}
              >
                {starting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Redirecting to checkout…
                  </>
                ) : (
                  // One accurate label in every arm. These used to be derived
                  // from `offer.priceLabel`, which read correctly at $1
                  // ("Continue for $1" named a price) and stopped reading
                  // correctly the moment the trial became free: "Continue for
                  // Free" and "Start building for Free" both describe a free
                  // product rather than the start of a $29/mo subscription.
                  //
                  // The A/B arms still differ in headline, sub-copy, timeline
                  // and the soft exit — what is no longer variable is whether
                  // the button tells the truth.
                  "Start free trial"
                )}
              </Button>

              {/* Soft exit. Before this, the only ways out were "pay" and the X —
                  which the QA data reads as the cause of the 3-second reflexive
                  dismissals. Giving the decline a name ("stay on Free") makes it
                  a choice rather than an escape, and routes through onDecline so
                  a deliberate decline is distinguishable from an X/backdrop
                  dismiss in analytics. */}
              {rewrite && (
                <Button
                  variant="ghost"
                  onClick={() => (onDecline ?? onClose)()}
                  disabled={starting}
                  className="mt-2 h-10 w-full rounded-xl text-[13.5px] font-semibold text-muted-foreground hover:text-foreground"
                >
                  Not now — stay on Free
                </Button>
              )}

              {/* The renewal terms, repeated immediately under the CTA.
                  Deliberately NOT just the reassurance it used to carry: "we'll
                  email you, cancel any time" describes how to STOP a charge
                  without ever naming it, which reads as friendly while leaving
                  the reader to infer that something is going to be taken. The
                  amount and the day lead, in foreground weight; the
                  cancellation follows. The full sentence still appears once in
                  the main hierarchy under the headline — this is the copy a
                  finger is hovering over at the moment of consent. */}
              <div className="mt-4 flex items-start gap-2.5 rounded-2xl p-4 ring-1 ring-foreground/10">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                <span className="text-[12.5px] leading-relaxed text-muted-foreground">
                  <span className="font-bold text-foreground">
                    {offer.priceLabel} today, then {offer.convertPriceLabel}/mo
                    automatically on day {offer.days}.
                  </span>{" "}
                  We&apos;ll email you before then. Cancel in one tap from
                  Billing — no charge if you cancel before your trial ends.
                </span>
              </div>

              {/* Eligibility fine print. The variant arm leads with the QA
                  wording ("New builders only — one $1 trial per account") but
                  KEEPS the card requirement and the trial-credit expiry, which
                  are material terms, not decoration. In particular the expiry is
                  why the benefit list above says "trial credits to use
                  immediately" and never that they roll over. */}
              <p className="mt-5 text-[13px] font-medium leading-relaxed text-muted-foreground/80">
                {rewrite ? (
                  <>
                    New builders only — one {offer.priceLabel} trial per account.
                    Card required. Trial credits are a one-time offer and expire
                    when the trial ends.
                  </>
                ) : (
                  <>
                    Card required. New builders only, one trial per account. Trial
                    credits are a one-time offer and expire when the trial ends.
                  </>
                )}
              </p>
            </div>
          </div>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * The 3-day timeline that doubles as the renewal disclosure (DMCC/FTC).
 *
 * `rewrite` (variant arm) drops the PRICE from the day-3 node. The ticket's
 * explicit rule is that the price and trial terms appear exactly once, and in
 * the variant arm the headline's terms line already states "then $29/mo. Cancel
 * any time." — so repeating the figure here is the same duplication the rewrite
 * set out to remove. The node itself stays, amber and all: the day-3 RENEWAL
 * (what happens, and when) is the disclosure, and it is unchanged. Only the
 * restated number goes, and only on this one surface.
 */
function TrialTimeline({
  offer,
  rewrite,
}: {
  offer: TrialOfferCopy;
  rewrite?: boolean;
}) {
  return (
    <div className="mt-4">
      <div className="mb-3 flex items-center justify-between">
        <b className="text-[12.5px] text-foreground">How your trial works</b>
        <span className="text-[11.5px] font-semibold text-muted-foreground">
          starts today
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1 text-center">
        <TimelineNode
          day="DAY 1"
          state="on"
          lines={[
            <b key="a" className="text-emerald-500 dark:text-emerald-400">
              {offer.priceLabel} today
            </b>,
            `${offer.credits} credits unlock`,
          ]}
        />
        <TimelineNode day="DAY 2" lines={["Build & launch"]} />
        <TimelineNode
          day={`DAY ${offer.days}`}
          state="charge"
          lines={[
            rewrite ? (
              <b key="a" className="text-amber-500">
                Pro renews
              </b>
            ) : (
              <span key="a">
                Renews at{" "}
                <b className="text-amber-500">{offer.convertPriceLabel}/mo</b>
              </span>
            ),
            `${offer.convertCredits} credits`,
          ]}
        />
      </div>
    </div>
  );
}

function TimelineNode({
  day,
  lines,
  state,
}: {
  day: string;
  lines: React.ReactNode[];
  state?: "on" | "charge";
}) {
  // DAY 1 is emerald to match its own "$1 today" label and the rest of the
  // modal. DAY 3 stays amber on purpose — that node is the renewal-charge
  // disclosure (DMCC/FTC), so it must not read as another reassuring green.
  const pip =
    state === "on"
      ? "border-emerald-600 bg-emerald-600"
      : state === "charge"
        ? "border-amber-500 bg-card"
        : "border-border bg-card";
  return (
    <div className="relative">
      <div className={`mx-auto h-4 w-4 rounded-full border-2 ${pip}`} />
      <div className="mt-2 text-[10.5px] font-bold tracking-wide text-muted-foreground">
        {day}
      </div>
      <div className="mt-1 text-[11.5px] font-semibold leading-tight text-muted-foreground">
        {lines.map((l, i) => (
          <div key={i}>{l}</div>
        ))}
      </div>
    </div>
  );
}
